/** Invalid input: the standard 400 validation response */
export class AuthValidationError extends Error {
    errors;
    constructor(errors) {
        super(Object.values(errors).flat().join(' ') || 'Invalid input.');
        this.errors = errors;
    }
}
/** A real failure (500, network); expected outcomes never end up here */
export class AuthRequestError extends Error {
    status;
    constructor(status) {
        super(status ? `The request failed (${status}).` : 'The server could not be reached.');
        this.status = status;
    }
}
/**
 * @param basePath the AuthController's route under the client's base URL
 * @param profilePath the MyProfileController's: My Profile and its device buttons
 * @param sessionPath the SessionController's: the session, the institution switch and the logout
 */
export function createAuthApi(http, basePath = '/auth', profilePath = '/profile', sessionPath = '/session') {
    /** The response, or null for a 401; a real failure throws */
    const send = async (method, path, data, base = basePath) => {
        let response;
        try {
            response = await http.request({ method, url: path ? `${base}/${path}` : base, data });
        }
        catch (error) {
            const status = statusOf(error);
            if (status === 401)
                return null;
            throw new AuthRequestError(status);
        }
        if (!response)
            throw new AuthRequestError(null);
        return response.status === 401 ? null : response;
    };
    /** A login call: expected failures are a 200 too, so a 400 is only invalid input */
    const call = async (method, path, data, base = basePath) => {
        const response = await send(method, path, data, base);
        if (!response)
            return null;
        if (response.status === 400)
            throw new AuthValidationError(validationErrors(response.data));
        if (response.status !== 200)
            throw new AuthRequestError(response.status);
        return response.data;
    };
    /** A My Profile read: its data, or null for a 401 */
    const read = async (path) => {
        const response = await send('get', path, undefined, profilePath);
        if (!response)
            return null;
        if (response.status !== 200)
            throw new AuthRequestError(response.status);
        return response.data;
    };
    /** A call outside the login (My Profile): 200 done, 400 not done and 429 too many attempts all carry its answer; a 400 without one is invalid input */
    const plain = async (path, data) => {
        const response = await send('post', path, data, profilePath);
        if (!response)
            return null;
        if ([200, 400, 429].includes(response.status) && typeof response.data?.message === 'string')
            return response.data;
        if (response.status === 400)
            throw new AuthValidationError(validationErrors(response.data));
        throw new AuthRequestError(response.status);
    };
    /** A SessionController call that answers the session: null for a 401 */
    const sessionCall = async (method, path) => {
        const response = await send(method, path, undefined, sessionPath);
        if (!response)
            return null;
        if (response.status !== 200)
            throw new AuthRequestError(response.status);
        return response.data;
    };
    return {
        resume: () => call('get', 'resume'),
        login: (email, password, rememberMe) => call('post', 'login', { email, password, rememberMe }),
        requestLoginLink: (email, rememberMe) => call('post', 'login/request-link', { email, rememberMe }),
        loginWithLink: (userId, token, rememberMe) => call('post', 'login/link', { userId, token, rememberMe }),
        setupMfa: (channel, phoneNumber) => call('post', 'mfa/setup', { channel, phoneNumber }),
        verifyMfa: (code) => call('post', 'mfa/verify', { code }),
        sendMfaCode: () => call('post', 'mfa/send'),
        backToMfaSetup: () => call('post', 'mfa/back-to-setup'),
        changeForcedPassword: (newPassword) => call('post', 'password/forced-change', { newPassword }),
        requestPasswordReset: (email) => call('post', 'password/request-reset', { email }),
        resetPassword: (userId, token, newPassword) => call('post', 'password/reset', { userId, token, newPassword }),
        getProfile: () => read(''),
        saveProfile: (profile) => plain('', profile),
        changePassword: (currentPassword, newPassword) => plain('password', { currentPassword, newPassword }),
        startMfaChange: (currentPassword, channel, phoneNumber) => plain('mfa', { currentPassword, channel, phoneNumber }),
        verifyMfaChange: (code) => plain('mfa/verify', { code }),
        startEmailChange: (currentPassword, newEmail) => plain('email', { currentPassword, newEmail }),
        verifyEmailChange: (code) => plain('email/verify', { code }),
        revokeThisDevice: () => call('post', 'devices/revoke-this', undefined, profilePath),
        revokeAllDevices: () => call('post', 'devices/revoke-all', undefined, profilePath),
        session: () => sessionCall('get', ''),
        logout: async () => {
            const response = await send('post', 'logout', undefined, sessionPath);
            if (response && response.status !== 204 && response.status !== 200)
                throw new AuthRequestError(response.status);
        },
        changeInstitution: (institutionId) => sessionCall('post', `institution/${institutionId}`),
    };
}
function statusOf(error) {
    const status = error?.response?.status;
    return typeof status === 'number' ? status : null;
}
/** ASP.NET's ValidationProblemDetails: { errors: { field: [messages] } } */
function validationErrors(data) {
    const errors = data?.errors;
    return errors && typeof errors === 'object' ? errors : {};
}
